import React from 'react'
import {
    App,
    Button,
    Empty,
    Input,
    Result,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    Tabs,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { RangePickerProps } from 'antd/es/date-picker'
import type { TablePaginationConfig } from 'antd/es/table/interface'
import {
    EditOutlined,
    EyeOutlined,
    FileImageOutlined,
    ReloadOutlined,
    SearchOutlined,
} from '@ant-design/icons'
import type { Dayjs } from 'dayjs'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { ReadonlyRangePicker } from '../components/common/DatePickerControls'
import {
    FilterActions,
    FilterBar,
    FilterField,
} from '../components/common/FilterBar'
import {
    PaginatedListCard,
    PAGINATED_LIST_TABLE_SIZE,
    buildPaginatedListPagination,
    renderPaginatedListText,
} from '../components/common/PaginatedListTable'
import {
    HistoryDetail,
    HistoryDeviceNameDisplay,
    RecognitionComparisonDetail,
} from '../components/history'
import { PageLayout } from '../components/layout'
import { usePermissions } from '../hooks/usePermissions'
import {
    getManualRecognitionListPage,
    getManualRecognitionRecord,
    updateManualRecognition,
} from '../services/historyService'
import { useModelRawClassFilterOptions } from '../services/pestTypeOptionsService'
import { localeService } from '../services/localeService'
import {
    HistoryListItem,
    ManualRecognitionObjectInput,
    HistoryPrediction,
    HistoryRecord,
    HistoryTotalStatus,
} from '../types/history'
import { formatManualRecognitionSummary } from '../utils/manualRecognitionDisplay'
import {
    formatRawClassDisplayName,
    formatRawClassSummary,
} from '../utils/rawClassDisplay'
import { projectPredictionsToManualRecognitionTypes } from '../utils/manualRecognitionDisplay'
import {
    getHistoryPaginationTotal,
    renderHistoryPaginationTotal,
} from '../utils/historyPagination'
import {
    areNormalizedSearchValuesEqual,
    normalizeSearchText,
    normalizeSearchValues,
} from '../utils/searchText'

interface ManualRecognitionFilterState {
    dateRange: [Dayjs, Dayjs] | null
    deviceKeyword: string
    pestTypes: string[]
}

type ManualRecognitionStatusFilter = 'marked' | 'unmarked'

const createDefaultFilters = (): ManualRecognitionFilterState => ({
    dateRange: null,
    deviceKeyword: '',
    pestTypes: [],
})

const createDefaultManualRecognitionStatus = (): ManualRecognitionStatusFilter =>
    'unmarked'

const MANUAL_RECOGNITION_TABLE_SCROLL_X = 1200
const MANUAL_RECOGNITION_MARKED_TABLE_SCROLL_X = 1400
const SUMMARY_SEGMENT_PATTERN = /^(.+?)(\((\d+)\))$/
const RESULT_SUMMARY_TAG_STYLE: React.CSSProperties = {
    display: 'inline-block',
    maxWidth: '100%',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    lineHeight: 1.4,
    verticalAlign: 'top',
}

const getSummaryTotal = (summary: string | null | undefined): number => {
    if (!summary) {
        return 0
    }

    return summary.split(',').reduce((total, part) => {
        const segment = part.trim()
        if (!segment) {
            return total
        }

        const match = segment.match(SUMMARY_SEGMENT_PATTERN)
        const count = Number(match?.[3] ?? '1')
        return total + (Number.isFinite(count) ? count : 1)
    }, 0)
}

const areFiltersEqual = (
    left: ManualRecognitionFilterState,
    right: ManualRecognitionFilterState
): boolean => {
    const leftDateRange = left.dateRange
    const rightDateRange = right.dateRange
    const hasSameDateRange =
        leftDateRange === rightDateRange ||
        (leftDateRange !== null &&
            rightDateRange !== null &&
            leftDateRange[0].valueOf() === rightDateRange[0].valueOf() &&
            leftDateRange[1].valueOf() === rightDateRange[1].valueOf())

    return (
        hasSameDateRange &&
        normalizeSearchText(left.deviceKeyword) ===
            normalizeSearchText(right.deviceKeyword) &&
        areNormalizedSearchValuesEqual(left.pestTypes, right.pestTypes)
    )
}

const ManualRecognition: React.FC = () => {
    const { message } = App.useApp()
    const { t, i18n } = useTranslation([
        'history',
        'common',
        'navigation',
        'admin',
    ])
    const { canAccessManualRecognitionPage, canSubmitManualRecognition } =
        usePermissions()
    const { options: modelRawClassOptions } = useModelRawClassFilterOptions(
        [],
        canAccessManualRecognitionPage
    )
    const pestTypeOptions = React.useMemo(
        () =>
            modelRawClassOptions.map((option) => ({
                value: option.value,
                label: formatRawClassDisplayName(option.value, t, option.label),
            })),
        [modelRawClassOptions, t]
    )
    const modelRawClassLocalizedNames = React.useMemo(
        () =>
            Object.fromEntries(
                modelRawClassOptions.map((option) => [
                    option.value.trim().toLowerCase(),
                    option.label,
                ])
            ),
        [modelRawClassOptions]
    )
    const [previewVisible, setPreviewVisible] = React.useState(false)
    const [previewLoading, setPreviewLoading] = React.useState(false)
    const [previewRecord, setPreviewRecord] =
        React.useState<HistoryRecord | null>(null)
    const [previewError, setPreviewError] = React.useState<string | null>(null)
    const previewLoadingIdRef = React.useRef<string | null>(null)
    const previewTargetRef = React.useRef<HistoryListItem | null>(null)
    const [comparisonVisible, setComparisonVisible] = React.useState(false)
    const [comparisonLoading, setComparisonLoading] = React.useState(false)
    const [comparisonRecord, setComparisonRecord] =
        React.useState<HistoryRecord | null>(null)
    const [comparisonError, setComparisonError] = React.useState<string | null>(
        null
    )
    const editablePreviewPredictions = React.useMemo(() => {
        if (!previewRecord) {
            return undefined
        }
        if (previewRecord.manual_recognition) {
            return previewRecord.manual_recognition.predictions
        }
        return projectPredictionsToManualRecognitionTypes(
            previewRecord.result.predictions,
            previewRecord.manual_type_options,
        )
    }, [previewRecord])
    const comparisonLoadingIdRef = React.useRef<string | null>(null)
    const comparisonTargetRef = React.useRef<HistoryListItem | null>(null)
    const [submitting, setSubmitting] = React.useState(false)
    const [draftFilters, setDraftFilters] =
        React.useState<ManualRecognitionFilterState>(() => createDefaultFilters())
    const [submittedFilters, setSubmittedFilters] =
        React.useState<ManualRecognitionFilterState>(() => createDefaultFilters())
    const [submittedManualRecognitionStatus, setSubmittedManualRecognitionStatus] =
        React.useState<ManualRecognitionStatusFilter>(() =>
            createDefaultManualRecognitionStatus()
        )
    const [records, setRecords] = React.useState<HistoryListItem[]>([])
    const [total, setTotal] = React.useState(0)
    const [totalStatus, setTotalStatus] =
        React.useState<HistoryTotalStatus>('ready')
    const [listError, setListError] = React.useState<string | null>(null)
    const [paginationMeta, setPaginationMeta] = React.useState({
        total_pages: 0,
        page_depth_limit: 0,
        max_accessible_page: 0,
        pagination_limited: false,
    })
    const [loading, setLoading] = React.useState(false)
    const [currentPage, setCurrentPage] = React.useState(1)
    const [pageSize, setPageSize] = React.useState(10)
    const [queryTrigger, setQueryTrigger] = React.useState(0)
    const previousQueryTriggerRef = React.useRef(queryTrigger)
    const previewRequestIdRef = React.useRef(0)
    const comparisonRequestIdRef = React.useRef(0)
    const listRequestIdRef = React.useRef(0)
    const messageRef = React.useRef(message)
    const tRef = React.useRef(t)

    React.useEffect(() => {
        messageRef.current = message
        tRef.current = t
    }, [message, t])

    const refreshRecords = React.useCallback(
        async (forceRefresh = false) => {
            listRequestIdRef.current += 1
            const requestId = listRequestIdRef.current
            setLoading(true)
            setListError(null)
            try {
                const response = await getManualRecognitionListPage(
                    {
                        page: currentPage,
                        page_size: pageSize,
                        start_timestamp: submittedFilters.dateRange
                            ? submittedFilters.dateRange[0].valueOf()
                            : undefined,
                        end_timestamp: submittedFilters.dateRange
                            ? submittedFilters.dateRange[1].valueOf()
                            : undefined,
                        device_name:
                            normalizeSearchText(
                                submittedFilters.deviceKeyword
                            ) || undefined,
                        pest_types:
                            submittedFilters.pestTypes.length > 0
                                ? submittedFilters.pestTypes
                                : undefined,
                        manual_recognition_status:
                            submittedManualRecognitionStatus,
                    },
                    forceRefresh
                )
                if (requestId !== listRequestIdRef.current) {
                    return
                }
                setRecords(response.items)
                setTotal(response.total)
                setTotalStatus(response.total_status ?? 'ready')
                setPaginationMeta({
                    total_pages: response.total_pages ?? 0,
                    page_depth_limit: response.page_depth_limit ?? 0,
                    max_accessible_page: response.max_accessible_page ?? 0,
                    pagination_limited: response.pagination_limited ?? false,
                })
            } catch (error) {
                if (requestId !== listRequestIdRef.current) {
                    return
                }
                console.error('Failed to refresh manual recognition records:', error)
                setRecords([])
                setTotal(0)
                setTotalStatus('ready')
                setPaginationMeta({
                    total_pages: 0,
                    page_depth_limit: 0,
                    max_accessible_page: 0,
                    pagination_limited: false,
                })
                setListError(tRef.current('history:messages.listLoadFailed'))
                messageRef.current.warning(
                    tRef.current('history:messages.listLoadFailed')
                )
            } finally {
                if (requestId === listRequestIdRef.current) {
                    setLoading(false)
                }
            }
        },
        [currentPage, pageSize, submittedFilters, submittedManualRecognitionStatus]
    )

    React.useEffect(() => {
        if (canAccessManualRecognitionPage) {
            const shouldForceRefresh =
                previousQueryTriggerRef.current !== queryTrigger
            previousQueryTriggerRef.current = queryTrigger
            void refreshRecords(shouldForceRefresh)
        }
    }, [canAccessManualRecognitionPage, queryTrigger, refreshRecords])

    const formatDate = React.useCallback(
        (timestamp: number) =>
            localeService.formatDate(timestamp, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            }),
        []
    )

    const handleDateRangeChange = React.useCallback<
        NonNullable<RangePickerProps['onChange']>
    >((dates) => {
        setDraftFilters((prevFilters) => ({
            ...prevFilters,
            dateRange:
                dates && dates[0] && dates[1]
                    ? ([dates[0], dates[1]] as [Dayjs, Dayjs])
                    : null,
        }))
    }, [])

    const handleSearch = React.useCallback(() => {
        const nextFilters: ManualRecognitionFilterState = {
            dateRange: draftFilters.dateRange,
            deviceKeyword: normalizeSearchText(draftFilters.deviceKeyword),
            pestTypes: normalizeSearchValues(draftFilters.pestTypes),
        }
        setCurrentPage(1)
        setSubmittedFilters(nextFilters)
        setQueryTrigger((value) => value + 1)
    }, [draftFilters])

    const handleReset = React.useCallback(() => {
        const nextFilters = createDefaultFilters()
        const shouldForceRefresh =
            currentPage === 1 &&
            areFiltersEqual(nextFilters, submittedFilters)

        setDraftFilters(nextFilters)
        setSubmittedFilters(nextFilters)
        setCurrentPage(1)

        if (shouldForceRefresh) {
            void refreshRecords(true)
        }
    }, [currentPage, refreshRecords, submittedFilters])

    const handleManualRecognitionStatusChange = React.useCallback(
        (status: string) => {
            const nextStatus =
                status === 'marked' ? 'marked' : createDefaultManualRecognitionStatus()
            setSubmittedManualRecognitionStatus(nextStatus)
            setCurrentPage(1)
        },
        []
    )

    const manualRecognitionStatusTabs = React.useMemo(
        () => [
            {
                key: 'unmarked',
                label: t('history:manualRecognitionTabs.unmarked'),
            },
            {
                key: 'marked',
                label: t('history:manualRecognitionTabs.marked'),
            },
        ],
        [t]
    )

    const openDetailModal = React.useCallback(
        async (record: HistoryListItem) => {
            if (record.media_expired) {
                message.warning(
                    t('history:messages.mediaExpiredCannotRecognize')
                )
                return
            }

            if (previewLoading && previewLoadingIdRef.current === record.id) {
                return
            }
            previewLoadingIdRef.current = record.id
            previewTargetRef.current = record
            setPreviewVisible(true)
            setPreviewRecord(null)
            setPreviewError(null)
            setPreviewLoading(true)
            previewRequestIdRef.current += 1
            const requestId = previewRequestIdRef.current

            const detailRecord = await getManualRecognitionRecord(record.id)
            if (requestId === previewRequestIdRef.current) {
                setPreviewRecord(detailRecord)
                setPreviewError(
                    detailRecord ? null : t('history:messages.detailLoadFailed')
                )
                setPreviewLoading(false)
                previewLoadingIdRef.current = null
            }
        },
        [message, previewLoading, t]
    )

    const openComparisonModal = React.useCallback(
        async (record: HistoryListItem) => {
            if (comparisonLoading && comparisonLoadingIdRef.current === record.id) {
                return
            }
            comparisonLoadingIdRef.current = record.id
            comparisonTargetRef.current = record
            setComparisonVisible(true)
            setComparisonRecord(null)
            setComparisonError(null)
            setComparisonLoading(true)
            comparisonRequestIdRef.current += 1
            const requestId = comparisonRequestIdRef.current

            const detailRecord = await getManualRecognitionRecord(record.id)
            if (requestId === comparisonRequestIdRef.current) {
                setComparisonRecord(detailRecord)
                setComparisonError(
                    detailRecord ? null : t('history:messages.detailLoadFailed')
                )
                setComparisonLoading(false)
                comparisonLoadingIdRef.current = null
            }
        },
        [comparisonLoading, t]
    )

    const columns = React.useMemo<ColumnsType<HistoryListItem>>(
        () => {
            const resultColumn: ColumnsType<HistoryListItem>[number] = {
                title: t('history:table.result'),
                key: 'effective_summary',
                width: 380,
                render: (_, record) => {
                    const modelSummary = formatRawClassSummary(
                        record.system_summary,
                        t,
                        {
                            withCode: true,
                            localizedNames: modelRawClassLocalizedNames,
                        }
                    )
                    const modelSummaryLabel = t(
                        'history:detail.modelRecognitionSummaryLabelWithTotal',
                        {
                            total: getSummaryTotal(record.system_summary),
                        }
                    )
                    const hasManualRecognition =
                        record.effective_source === 'manual'
                    const manualSummaryTotal = getSummaryTotal(
                        record.effective_summary
                    )
                    const manualSummary =
                        hasManualRecognition
                            ? formatManualRecognitionSummary(
                                  record.effective_summary,
                                  record.manual_type_options,
                                  i18n.resolvedLanguage || i18n.language
                              )
                            : null
                    const manualSummaryText = hasManualRecognition
                        ? manualSummary ||
                          t('history:detail.manualRecognitionZeroCount')
                        : t('history:detail.manualRecognitionEmpty')
                    const manualSummaryLabel = hasManualRecognition
                        ? t(
                              'history:detail.manualRecognitionSummaryLabelWithTotal',
                              { total: manualSummaryTotal }
                          )
                        : t('history:detail.manualRecognitionSummaryLabel')
                    const shouldShowManualSummary =
                        submittedManualRecognitionStatus === 'marked'

                    return (
                        <Space
                            direction="vertical"
                            size={4}
                            style={{ width: '100%', alignItems: 'flex-start' }}
                        >
                            <Tag
                                color={modelSummary ? 'green' : 'orange'}
                                style={RESULT_SUMMARY_TAG_STYLE}
                                title={`${modelSummaryLabel}: ${
                                    modelSummary || t('history:messages.noDetection')
                                }`}
                            >
                                {modelSummaryLabel}:{' '}
                                {modelSummary || t('history:messages.noDetection')}
                            </Tag>
                            {shouldShowManualSummary ? (
                                <Tag
                                    color={
                                        hasManualRecognition ? 'blue' : 'default'
                                    }
                                    style={RESULT_SUMMARY_TAG_STYLE}
                                    title={`${manualSummaryLabel}: ${manualSummaryText}`}
                                >
                                    {manualSummaryLabel}:{' '}
                                    {manualSummaryText}
                                </Tag>
                            ) : null}
                        </Space>
                    )
                },
            }
            const manualRecognitionTimeColumn: ColumnsType<HistoryListItem>[number] =
                {
                    title: t('history:table.manualRecognitionTime'),
                    key: 'manual_recognition_updated_at',
                    width: 180,
                    render: (_, record) =>
                        record.manual_recognition_updated_at !== null &&
                        record.manual_recognition_updated_at !== undefined
                            ? formatDate(record.manual_recognition_updated_at)
                            : t('history:messages.taskTimeUnavailable'),
                }

            return [
                {
                    title: t('history:table.fileName'),
                    dataIndex: 'filename',
                    key: 'filename',
                    width: 240,
                    ellipsis: true,
                    render: (text: string) => (
                        <Space style={{ maxWidth: '100%' }}>
                            <FileImageOutlined />
                            {renderPaginatedListText(text)}
                        </Space>
                    ),
                },
                {
                    title: t('history:table.deviceName'),
                    key: 'device_name',
                    width: 220,
                    ellipsis: true,
                    render: (_, record) => (
                        <HistoryDeviceNameDisplay
                            record={record}
                            fallback={t('history:messages.deviceUnavailable')}
                        />
                    ),
                },
                {
                    title: t('history:table.uploadTime'),
                    key: 'timestamp',
                    width: 180,
                    render: (_, record) => formatDate(record.timestamp),
                },
                ...(submittedManualRecognitionStatus === 'marked'
                    ? [manualRecognitionTimeColumn]
                    : []),
                resultColumn,
                {
                    title: t('history:table.actions'),
                    key: 'action',
                    width: 200,
                    fixed: 'right' as const,
                    render: (_, record) => (
                        <Space size={8}>
                            <Button
                                size="small"
                                icon={<EyeOutlined />}
                                loading={
                                    comparisonLoading &&
                                    comparisonLoadingIdRef.current === record.id
                                }
                                disabled={
                                    comparisonLoading &&
                                    comparisonLoadingIdRef.current === record.id
                                }
                                onClick={() => {
                                    void openComparisonModal(record)
                                }}
                            >
                                {t('history:actions.viewDetails')}
                            </Button>
                            <Button
                                type="primary"
                                size="small"
                                icon={<EditOutlined />}
                                loading={
                                    previewLoading &&
                                    previewLoadingIdRef.current === record.id
                                }
                                disabled={
                                    previewLoading &&
                                    previewLoadingIdRef.current === record.id
                                }
                                onClick={() => {
                                    void openDetailModal(record)
                                }}
                            >
                                {t('history:actions.manualRecognize')}
                            </Button>
                        </Space>
                    ),
                },
            ]
        },
        [
            comparisonLoading,
            formatDate,
            i18n.language,
            i18n.resolvedLanguage,
            modelRawClassLocalizedNames,
            openComparisonModal,
            openDetailModal,
            previewLoading,
            submittedManualRecognitionStatus,
            t,
        ]
    )

    if (!canAccessManualRecognitionPage) {
        return (
            <PageLayout
                title={t('history:manualRecognitionTitle')}
                breadcrumb={[
                    { title: t('navigation:breadcrumb.manualRecognition') },
                ]}
            >
                <Result
                    status="403"
                    title={t('common:featureNotAvailable')}
                    subTitle={t(
                        'history:messages.manualRecognitionAccessDenied'
                    )}
                    extra={
                        <Button type="primary">
                            <Link to="/">{t('common:backToHome')}</Link>
                        </Button>
                    }
                />
            </PageLayout>
        )
    }

    return (
        <PageLayout
            title={t('history:manualRecognitionTitle')}
            breadcrumb={[
                { title: t('navigation:breadcrumb.manualRecognition') },
            ]}
        >
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Tabs
                    activeKey={submittedManualRecognitionStatus}
                    items={manualRecognitionStatusTabs.map((item) => ({
                        key: item.key,
                        label: item.label,
                        children: null,
                    }))}
                    onChange={handleManualRecognitionStatusChange}
                    style={{ marginBottom: 0 }}
                />

                <FilterBar>
                    <FilterField label={t('history:filters.uploadTime')}>
                        <ReadonlyRangePicker
                            showTime
                            value={draftFilters.dateRange}
                            onChange={handleDateRangeChange}
                            format="YYYY/MM/DD HH:mm:ss"
                        />
                    </FilterField>

                    <FilterField label={t('history:filters.deviceSearch')}>
                        <Input
                            allowClear
                            prefix={<SearchOutlined />}
                            value={draftFilters.deviceKeyword}
                            onChange={(event) => {
                                setDraftFilters((prevFilters) => ({
                                    ...prevFilters,
                                    deviceKeyword: event.target.value,
                                }))
                            }}
                            onPressEnter={handleSearch}
                            placeholder={t(
                                'history:filters.deviceSearchPlaceholder'
                            )}
                        />
                    </FilterField>

                    <FilterField label={t('history:filters.pestType')}>
                        <Select<string[]>
                            allowClear
                            aria-label={t('history:filters.pestType')}
                            maxTagCount="responsive"
                            mode="multiple"
                            value={draftFilters.pestTypes}
                            options={pestTypeOptions}
                            onChange={(value) => {
                                setDraftFilters((prevFilters) => ({
                                    ...prevFilters,
                                    pestTypes: value,
                                }))
                            }}
                            optionFilterProp="label"
                            placeholder={t(
                                'history:filters.pestTypePlaceholder'
                            )}
                            showSearch
                        />
                    </FilterField>

                    <FilterActions>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={handleSearch}
                            loading={loading}
                            disabled={loading}
                        >
                            {t('history:actions.search')}
                        </Button>
                        <Button onClick={handleReset} disabled={loading}>
                            {t('history:actions.reset')}
                        </Button>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => refreshRecords(true)}
                            loading={loading}
                            disabled={loading}
                        >
                            {t('history:actions.refresh')}
                        </Button>
                    </FilterActions>
                </FilterBar>

                <PaginatedListCard>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: 48 }}>
                            <Spin size="large" />
                        </div>
                    ) : listError && records.length === 0 ? (
                        <Result
                            status="warning"
                            title={listError}
                            extra={
                                <Button onClick={() => refreshRecords(true)}>
                                    {t('history:actions.refresh')}
                                </Button>
                            }
                        />
                    ) : records.length === 0 ? (
                        <Empty description={t('history:messages.noRecords')} />
                    ) : (
                        <Table
                            rowKey="id"
                            size={PAGINATED_LIST_TABLE_SIZE}
                            columns={columns}
                            dataSource={records}
                            tableLayout="fixed"
                            scroll={{
                                x:
                                    submittedManualRecognitionStatus === 'marked'
                                        ? MANUAL_RECOGNITION_MARKED_TABLE_SCROLL_X
                                        : MANUAL_RECOGNITION_TABLE_SCROLL_X,
                            }}
                            onChange={(pagination: TablePaginationConfig) => {
                                const resolvedPage =
                                    pagination.current ?? currentPage
                                const resolvedPageSize =
                                    pagination.pageSize ?? pageSize
                                if (
                                    resolvedPage !== currentPage ||
                                    resolvedPageSize !== pageSize
                                ) {
                                    const nextPage =
                                        resolvedPageSize !== pageSize
                                            ? 1
                                            : resolvedPage
                                    setPageSize(resolvedPageSize)
                                    setCurrentPage(nextPage)
                                }
                            }}
                            pagination={buildPaginatedListPagination({
                                current: currentPage,
                                pageSize,
                                total: getHistoryPaginationTotal(
                                    paginationMeta,
                                    pageSize,
                                    total
                                ),
                                showTotal: () =>
                                    renderHistoryPaginationTotal(
                                        (key, options) =>
                                            t(`history:${key}`, options),
                                        {
                                            total,
                                            totalStatus,
                                            pageSize,
                                            meta: paginationMeta,
                                        }
                                    ),
                            })}
                        />
                    )}
                </PaginatedListCard>
            </Space>

            <RecognitionComparisonDetail
                visible={comparisonVisible}
                record={comparisonRecord}
                rawClassLocalizedNames={modelRawClassLocalizedNames}
                loading={comparisonLoading}
                error={comparisonError}
                onClose={() => {
                    comparisonRequestIdRef.current += 1
                    setComparisonVisible(false)
                    setComparisonLoading(false)
                    setComparisonError(null)
                    comparisonLoadingIdRef.current = null
                }}
                onRetry={() => {
                    const target = comparisonTargetRef.current
                    if (target) {
                        void openComparisonModal(target)
                    }
                }}
                formatDate={formatDate}
                titleKey="actions.viewDetails"
                showSecondaryResult={
                    submittedManualRecognitionStatus === 'marked'
                }
            />

            <HistoryDetail
                visible={previewVisible}
                record={previewRecord}
                loading={previewLoading}
                error={previewError}
                editable={canSubmitManualRecognition}
                canExport={false}
                annotationActionPlacement="modal-footer"
                zoomControlPlacement="image-overlay"
                defaultPredictionConfidence={0.9}
                readonlyPredictionSource="effective"
                useManualRecognitionTypes
                bypassCapabilityFallback={canSubmitManualRecognition}
                reviewSubmitting={submitting}
                exportSubmitting={false}
                editablePredictions={editablePreviewPredictions}
                editableTitleKey="modal.manualRecognitionTitle"
                saveButtonTextKey="actions.saveManualRecognition"
                resetButtonTextKey="actions.reset"
                showImageNormalAction={false}
                saveSuccessMessageKey="messages.manualRecognitionSaved"
                saveFailureMessageKey="messages.manualRecognitionSaveFailed"
                onClose={() => {
                    previewRequestIdRef.current += 1
                    setPreviewVisible(false)
                    setPreviewLoading(false)
                    setPreviewError(null)
                    previewLoadingIdRef.current = null
                }}
                onRetry={() => {
                    const target = previewTargetRef.current
                    if (target) {
                        void openDetailModal(target)
                    }
                }}
                onUpdateReview={async () => null}
                onSaveEditablePredictions={async (
                    predictions: HistoryPrediction[]
                ) => {
                    if (!previewRecord) {
                        return null
                    }

                    setSubmitting(true)
                    try {
                        const manualPredictions = predictions.map(
                            (prediction): ManualRecognitionObjectInput => {
                                if (!prediction.manual_type_key || !prediction.bbox) {
                                    throw new Error(
                                        'Manual recognition prediction is missing its type or bbox'
                                    )
                                }
                                return {
                                    manual_type_key: prediction.manual_type_key,
                                    bbox: prediction.bbox,
                                    ...(Number.isFinite(prediction.confidence)
                                        ? { confidence: prediction.confidence }
                                        : {}),
                                }
                            }
                        )
                        const updatedRecord = await updateManualRecognition(
                            previewRecord.id,
                            {
                                predictions: manualPredictions,
                            }
                        )
                        if (updatedRecord) {
                            setPreviewRecord(updatedRecord)
                            void refreshRecords(true)
                        }
                        return updatedRecord
                    } catch {
                        return null
                    } finally {
                        setSubmitting(false)
                    }
                }}
                onExport={async () => undefined}
                formatDate={formatDate}
            />
        </PageLayout>
    )
}

export default ManualRecognition
